import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { debounce } from 'lodash';
import classNames from 'classnames';
import { DEFAULT_TAG_COLOR } from '../../lib/colors';
import InputGroup from '../InputGroup';
import RoundedRectangle from '../../svgs/RoundedRectangle';
import { SvgIconComponent } from '../../types/svgs';

const ColorSwatchWithBorder: SvgIconComponent = ({
  className,
  customColor,
}) => (
  <div
    className={classNames('size-7 relative overflow-hidden rounded', className)}
  >
    <RoundedRectangle customColor={customColor} />
    <div
      className="pointer-events-none absolute inset-0 rounded border border-black"
      aria-hidden
    />
  </div>
);

const PICKER_SIZE = 241.5;
const GAP = 8;
const HEX_PATTERN = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

type ProgramTagColorSelectorProps = {
  selectedColor: string;
  onColorChange?: (color: string) => void;
  onPickerClose?: () => void;
  isPickerOpen?: boolean;
  onPickerOpenChange?: (open: boolean) => void;
  className?: string;
};

const ProgramTagColorSelector: React.FC<ProgramTagColorSelectorProps> = ({
  selectedColor,
  onColorChange,
  onPickerClose,
  isPickerOpen,
  onPickerOpenChange,
  className,
}) => {
  const [internalShowPicker, setInternalShowPicker] = useState(false);
  const showPicker =
    onPickerOpenChange !== undefined
      ? isPickerOpen ?? false
      : internalShowPicker;
  const [pickerStyle, setPickerStyle] = useState<React.CSSProperties>({});
  const [inputValue, setInputValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const displayColor = selectedColor || DEFAULT_TAG_COLOR;

  const onColorChangeRef = useRef(onColorChange);
  onColorChangeRef.current = onColorChange;

  const debouncedColorChange = useMemo(
    () =>
      debounce((newColor: string) => {
        onColorChangeRef.current?.(newColor);
      }, 300),
    [],
  );

  useEffect(() => () => debouncedColorChange.cancel(), [debouncedColorChange]);

  useEffect(() => {
    setInputValue(displayColor);
  }, [displayColor]);

  const handlePickerClose = useCallback(() => {
    debouncedColorChange.flush();
    onPickerOpenChange
      ? onPickerOpenChange(false)
      : setInternalShowPicker(false);
    setInputValue(displayColor);
    onPickerClose?.();
  }, [debouncedColorChange, displayColor, onPickerClose, onPickerOpenChange]);

  useEffect(() => {
    if (!showPicker) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handlePickerClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showPicker, handlePickerClose]);

  const updatePickerPosition = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openAbove = spaceBelow < PICKER_SIZE + GAP && spaceAbove > spaceBelow;

    let left = rect.left;
    if (left + PICKER_SIZE > window.innerWidth - GAP) {
      left = window.innerWidth - PICKER_SIZE - GAP;
    }
    left = Math.max(GAP, left);

    let top = openAbove ? rect.top - PICKER_SIZE - GAP : rect.bottom + GAP;
    top = Math.max(GAP, Math.min(top, window.innerHeight - PICKER_SIZE - GAP));

    setPickerStyle({
      position: 'fixed',
      left,
      top,
      zIndex: 60,
    });
  }, []);

  const handleOpenPicker = useCallback(() => {
    updatePickerPosition();
    onPickerOpenChange ? onPickerOpenChange(true) : setInternalShowPicker(true);
  }, [onPickerOpenChange, updatePickerPosition]);

  useLayoutEffect(() => {
    if (showPicker) {
      updatePickerPosition();
    }
  }, [showPicker, updatePickerPosition]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedColorChange.cancel();
      let value = e.target.value.replace(/^#+/, '');
      if (value) {
        value = '#' + value;
      }
      setInputValue(value || '#');
      if (HEX_PATTERN.test(value)) {
        onColorChange?.(value);
      }
    },
    [onColorChange],
  );

  const handleInputBlur = useCallback(() => {
    if (!HEX_PATTERN.test(inputValue)) {
      setInputValue(displayColor);
    }
  }, [inputValue, displayColor]);

  return (
    <div ref={containerRef} className={classNames('relative', className)}>
      <InputGroup
        IconLeft={ColorSwatchWithBorder}
        iconLeftProps={{ customColor: displayColor }}
        iconLeftClassName="ml-1.5 cursor-pointer"
        iconLeftOnClick={handleOpenPicker}
        labelHidden
        inputSize="small"
        containerClassName="w-[170px] min-w-[170px]"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onFocus={(e) => {
          e.target.select();
          handleOpenPicker();
        }}
        placeholder={DEFAULT_TAG_COLOR}
        maxLength={7}
        className="rounded-lg pl-[54px]"
      />
      {showPicker &&
        createPortal(
          <div className="fixed inset-0" style={{ zIndex: 55 }}>
            <div
              className="fixed inset-0"
              onClick={handlePickerClose}
              aria-hidden="true"
            />
            <div
              className="rounded-lg bg-white p-4 shadow-lg"
              style={pickerStyle}
              role="dialog"
              aria-label="Color picker"
            >
              <HexColorPicker
                color={displayColor}
                onChange={debouncedColorChange}
                className="min-h-[200px] min-w-[200px] rounded-lg"
              />
              <div className="mt-3">
                <HexColorInput
                  color={displayColor}
                  onChange={debouncedColorChange}
                  prefixed
                  className="w-full rounded border-2 border-neutral-75 bg-neutral-50 px-3 py-2 text-caption text-neutral-150 focus:border-secondary-50 focus:outline-none"
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ProgramTagColorSelector;
